// Admin › Changelog › Platform: the page's state for the Platform tab. What is
// open lives in the page address like the Client tab's, under repo=platform:
// open=<day>, apps=<day>.<part> (or pending.<part>), app=<part>,…, view=pending.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChangelogHeader, TabSwitch, type Repo } from './Header';
import { DayCard, LiveParts, PlatformPendingList } from './Platform';
import { PartIcon } from './PartIcon';
import { AuthorMark, CommitChip, LoadingTimeline, Notice, PrChip, RetryButton, TextButton } from './parts';
import { RegisterContext } from './KbChanges';
import type { CopyFormat } from './UpdateCard';
import { PENDING_ID, SEARCH_MIN, appNames, byMonth, highlight, nzShortDay, nzSince, nzTime, readViewFor, writeView, type ViewState } from '../data/changelog';
import { platformDays, searchPlatform, usePlatformChangelog, type PlatformDay, type PlatformHit } from '../data/platform';

const withRepo = (v: ViewState) => {
  const p = new URLSearchParams({ repo: 'platform' });
  for (const [k, val] of writeView(v)) p.set(k, val);
  return p;
};

export function PlatformTab({ format, onFormat, onRepo }: { format: CopyFormat; onFormat: (f: CopyFormat) => void; onRepo: (r: Repo) => void }) {
  const { data, isLoading, isError, refetch, isFetching } = usePlatformChangelog(true);
  const [params, setParams] = useSearchParams();
  const [now] = useState(() => Date.now());
  const live = useMemo(() => data?.live ?? [], [data]);
  const days = useMemo(() => platformDays(data?.deploys ?? [], live), [data, live]);
  const newest = days[0];
  const view = useMemo(() => readViewFor(params, newest?.id ?? null, newest?.parts[0]?.id ?? null), [params, newest]);

  const change = (edit: (v: ViewState) => void) => {
    const next: ViewState = { open: new Set(view.open), apps: new Set(view.apps), only: new Set(view.only), q: view.q, tab: view.tab };
    edit(next);
    setParams(withRepo(next), { replace: true });
  };

  const [scrollTo, setScrollTo] = useState<string | null>(() => (params.has('open') && params.get('open')?.split(',').length === 1 ? params.get('open') : null));
  useEffect(() => {
    if (!scrollTo || !data) return;
    const el = document.getElementById(scrollTo.startsWith(`${PENDING_ID}-`) ? scrollTo : `day-${scrollTo}`);
    if (el) el.scrollIntoView({ block: 'start' });
    setScrollTo(null);
  }, [scrollTo, data]);

  // Parts chosen in "Live now": only days with one of them, showing only them.
  const only = view.only;
  const chosen = useMemo(() => (only.size ? (id: string) => only.has(id) : () => true), [only]);
  const visible = useMemo(() => days.map((d) => ({ ...d, parts: d.parts.filter((p) => chosen(p.id)) })).filter((d) => d.parts.length), [days, chosen]);
  const pending = useMemo(() => (data?.pending ?? []).filter((p) => chosen(p.part)), [data, chosen]);
  const searching = view.q.trim().length >= SEARCH_MIN;
  const hits = useMemo(() => (searching ? searchPlatform(visible, pending, view.q) : []), [searching, visible, pending, view.q]);
  const liveSha = useMemo(() => Object.fromEntries(live.map((p) => [p.id, p.sha])), [live]);
  const filterName = appNames(live.filter((p) => only.has(p.id)));

  const showTab = (tab: ViewState['tab']) => change((v) => {
    v.tab = tab;
    if (tab === 'pending' && pending[0] && !pending.some((p) => v.apps.has(`${PENDING_ID}.${p.part}`))) v.apps.add(`${PENDING_ID}.${pending[0].part}`);
  });

  const linkTo = (d: PlatformDay) => () => {
    const parts = [...view.apps].filter((k) => k.startsWith(`${d.id}.`));
    const p = withRepo({ open: new Set([d.id]), apps: new Set(parts.length ? parts : [`${d.id}.${d.parts[0]?.id}`]), only: new Set(), q: '', tab: 'sent' });
    return `${window.location.origin}${window.location.pathname}?${p.toString()}`;
  };

  return (
    <RegisterContext.Provider value={data?.register ?? null}>
    <div className="max-w-[1120px] space-y-6 px-4 pb-16 pt-6 sm:px-6 lg:px-10 lg:pt-7">
      <ChangelogHeader repo="platform" onRepo={onRepo} q={view.q} onQ={(q) => change((v) => { v.q = q; })} format={format} onFormat={onFormat} />

      {data?.github === 'not_configured' && (
        <Notice tone="warning"><strong className="font-semibold text-white">The platform changelog isn't connected.</strong> The API needs its GitHub app settings to read the platform's deploys.</Notice>
      )}
      {data?.github === 'no_access' && (
        <Notice tone="warning"><strong className="font-semibold text-white">The GitHub app can't read the platform repo yet.</strong> Give the Vortex changelog reader app vortexnav-platform, with Checks: Read and Actions: Read.</Notice>
      )}
      {(data?.github === 'error' || isError) && (
        <Notice tone="error" action={<RetryButton onClick={() => void refetch()} busy={isFetching} />}>
          <strong className="font-semibold text-white">Couldn't load the platform's deploys.</strong> {data?.githubError ?? 'Check your connection and try again.'}
        </Notice>
      )}

      {isLoading && <LoadingTimeline />}

      {data && data.github === 'ok' && (
        <>
          <LiveParts parts={live} chosen={only} now={now} busiest={data.busiest} onPick={(id) => change((v) => { if (v.only.has(id)) v.only.delete(id); else v.only.add(id); })} />

          {only.size > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.07] py-1.5 pl-4 pr-1.5 text-[15px] text-slate-200">
              <span className="min-w-0 flex-1 py-1">
                Showing <strong className="font-semibold text-white">{filterName}</strong> only · {view.tab === 'pending'
                  ? (pending.length ? 'changes not live yet' : 'everything is live')
                  : `${visible.length} ${visible.length === 1 ? 'day' : 'days'}`}
              </span>
              <button type="button" onClick={() => change((v) => { v.only = new Set(); })} className="min-h-[36px] rounded-md px-2.5 text-sm font-medium text-cyan-300 hover:bg-slate-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
                Show every part
              </button>
            </div>
          )}

          {searching ? (
            <PlatformSearchResults
              q={view.q}
              hits={hits}
              onOpen={(h) => {
                if (h.where === PENDING_ID) {
                  change((v) => { v.q = ''; v.tab = 'pending'; v.apps.add(`${PENDING_ID}.${h.part}`); });
                  setScrollTo(`${PENDING_ID}-${h.part}`);
                  return;
                }
                change((v) => { v.q = ''; v.tab = 'sent'; v.open.add(h.where); v.apps.add(`${h.where}.${h.part}`); });
                setScrollTo(h.where);
              }}
            />
          ) : (
            <TabSwitch tab={view.tab} sent={visible.length} pending={pending.length} onPick={showTab} labels={['Deployed', 'Not live yet']} />
          )}

          {!searching && view.tab === 'pending' && (
            <PlatformPendingList
              pending={pending}
              isOpen={(part) => view.apps.has(`${PENDING_ID}.${part}`)}
              onToggle={(part) => change((v) => { const k = `${PENDING_ID}.${part}`; if (v.apps.has(k)) v.apps.delete(k); else v.apps.add(k); })}
              format={format}
              filtered={only.size}
            />
          )}

          {!searching && view.tab === 'sent' && (
            <>
              <div className="-mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-slate-400">
                  {visible.reduce((n, d) => n + d.parts.reduce((m, p) => m + p.deploys.length, 0), 0)} deploys since {nzSince(data.since)} · Times are New Zealand time
                </span>
                {visible.length > 0 && (
                  <span className="flex gap-1">
                    <TextButton onClick={() => change((v) => {
                      v.open = new Set(visible.map((d) => d.id));
                      v.apps = new Set(visible.flatMap((d) => d.parts.map((p) => `${d.id}.${p.id}`)));
                    })}>Expand all</TextButton>
                    <TextButton onClick={() => change((v) => { v.open = new Set(); v.apps = new Set(); })}>Collapse all</TextButton>
                  </span>
                )}
              </div>

              {visible.length === 0 && (
                <p className="text-[15px] text-slate-400">{only.size ? `Nothing from ${appNames(live.filter((p) => only.has(p.id)), 'or')} was deployed in this time.` : 'Nothing has been deployed in this time.'}</p>
              )}

              {byMonth(visible.map((d) => ({ ...d, firstOfferedAt: d.last }))).map((m) => (
                <section key={m.label} aria-labelledby={`month-${m.label}`} className="space-y-3">
                  <h2 id={`month-${m.label}`} className="flex items-baseline gap-2.5 text-[15px] font-semibold text-slate-200">
                    {m.label} <span className="text-sm font-medium text-slate-500">{m.otas.length} {m.otas.length === 1 ? 'day' : 'days'}</span>
                  </h2>
                  {m.otas.map((d) => (
                    <DayCard
                      key={d.id}
                      day={days.find((x) => x.id === d.id)!}
                      parts={d.parts}
                      latest={d.id === newest?.id}
                      open={view.open.has(d.id)}
                      isPartOpen={(part) => view.apps.has(`${d.id}.${part}`)}
                      onToggle={() => change((v) => {
                        if (v.open.has(d.id)) { v.open.delete(d.id); return; }
                        v.open.add(d.id);
                        if (d.parts[0] && !d.parts.some((p) => v.apps.has(`${d.id}.${p.id}`))) v.apps.add(`${d.id}.${d.parts[0].id}`);
                      })}
                      onTogglePart={(part) => change((v) => { const k = `${d.id}.${part}`; if (v.apps.has(k)) v.apps.delete(k); else v.apps.add(k); })}
                      linkTo={linkTo(d)}
                      format={format}
                      now={now}
                      liveSha={liveSha}
                    />
                  ))}
                </section>
              ))}
            </>
          )}
        </>
      )}
    </div>
    </RegisterContext.Provider>
  );
}

function PlatformSearchResults({ q, hits, onOpen }: { q: string; hits: PlatformHit[]; onOpen: (h: PlatformHit) => void }) {
  return (
    <section aria-label="Search results" className="space-y-3">
      <p className="text-[15px] text-slate-300" aria-live="polite">
        {hits.length ? (
          <><strong className="font-semibold text-white">{hits.length} {hits.length === 1 ? 'change' : 'changes'}</strong> {hits.length === 1 ? 'matches' : 'match'} “{q.trim()}”</>
        ) : (
          <>Nothing matches “{q.trim()}”. Try a shorter or different word.</>
        )}
      </p>
      {hits.length > 0 && (
        <ul className="divide-y divide-slate-700/60 overflow-hidden rounded-2xl border border-slate-700 bg-slate-800/50">
          {hits.map((h) => (
            // On phones the links drop under the text, lined up with it, so the text keeps the width.
            <li key={`${h.where}-${h.part}-${h.change.sha}`} className="flex flex-wrap items-start gap-x-3 gap-y-2 px-4 py-3">
              <PartIcon part={h.part} size={28} />
              <div className="min-w-0 flex-1 basis-[calc(100%-40px)] sm:basis-0">
                <div className="text-[15px] leading-snug text-slate-200">
                  {highlight(h.change.title, q).map((p, j) => (p.hit ? <mark key={j} className="rounded-[3px] bg-yellow-400/25 px-0.5 text-amber-100">{p.text}</mark> : <span key={j}>{p.text}</span>))}
                </div>
                {h.change.summary && (
                  <div className="mt-0.5 line-clamp-2 text-sm leading-relaxed text-slate-500 sm:line-clamp-none">
                    {highlight(h.change.summary, q).map((p, j) => (p.hit ? <mark key={j} className="rounded-[3px] bg-yellow-400/25 px-0.5 text-amber-100">{p.text}</mark> : <span key={j}>{p.text}</span>))}
                  </div>
                )}
                <div className="mt-1 text-sm text-slate-400">
                  {h.name} · {h.at ? `live ${nzShortDay(h.at)}, ${nzTime(h.at)}` : 'not live yet'}
                </div>
              </div>
              <span className="ml-10 flex flex-none items-center gap-2 sm:ml-0">
                {h.change.author && <AuthorMark author={h.change.author} />}
                {h.change.pr && <PrChip number={h.change.pr.number} url={h.change.pr.url} hit={h.by === 'pr'} />}
                <CommitChip sha={h.change.sha} url={h.change.url} hit={h.by === 'sha'} />
                <button type="button" onClick={() => onOpen(h)} className="min-h-[40px] rounded px-1 text-sm font-medium text-cyan-400 hover:text-cyan-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
                  Open
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
